'use client';
import { AreaChart } from '@tremor/react';
import React from 'react';
import { HeroPatternSmall } from '../landing/hero-pattern-small';
import { IconCloudBolt, IconCloudUpload, IconShieldBolt } from '@tabler/icons-react';

import { motion } from 'framer-motion';
import { Spotlight } from '../ui/spotlight';
import Image from 'next/image';
import Link from 'next/link';

export const SkeletonThree = () => {
  return (
    <div className="h-60 w-full flex flex-col items-center bg-primary-400 justify-center relative">
      <IconShieldBolt className="h-16 w-16 text-primary-200 fill-primary-500 block" />
      <Spotlight className="-top-20 left-0 md:-top-20" fill="white" />
      <HeroPatternSmall />
      <div className="absolute inset-x-0 bottom-0 h-20 bg-gray-100 dark:bg-zinc-800 [mask-image:linear-gradient(to_bottom,transparent,white)] pointer-events-none" />
    </div>
  );
};

export const SkeletonTwo = () => {
  return (
    <div className="h-60 w-full relative [perspective:1000px] [transform-style:preserve-3d]">
      <motion.div
        style={{
          rotateX: 45,
        }}
      >
        <HeroPatternSmall className="transform scale-[1.8]" />
        <SVG className="absolute top-0 inset-x-0 transform rotate-180" />
        <SVG className="absolute bottom-0 right-0" />
        <div className="mt-4 bg-white relative h-20 shadow-2xl shadow-blue-500  w-20 rounded-md m-auto flex items-center justify-center backdrop-blur-sm z-50 border border-neutral-300">
          <IconCloudBolt className="h-10 w-10 text-neutral-500" />
          <div className="absolute -bottom-1 -z-10  inset-x-0 h-3 bg-gradient-to-r rounded-br-lg rounded-bl-lg from-neutral-300 via-neutral-400 from-[20%] to-neutral-300 to-[80%]" />
        </div>
        <div className="mt-8 bg-white relative shadow-2xl shadow-emerald-500   h-20 w-20 rounded-md m-auto flex items-center justify-center backdrop-blur-sm z-50 border border-neutral-300">
          <IconCloudUpload className="h-10 w-10 text-neutral-500" />
          <div className="absolute -bottom-1 -z-10  inset-x-0 h-3 bg-gradient-to-r rounded-br-lg rounded-bl-lg from-neutral-300 via-neutral-400 from-[20%] to-neutral-300 to-[80%]" />
        </div>
      </motion.div>
    </div>
  );
};

const SVG = ({ className }: { className?: string }) => {
  const width = 236;
  const height = 68;
  const path =
    'M0.5 0.5H89C90.6569 0.5 92 1.84315 92 3.5V29C92 30.6569 93.3431 32 95 32H148.5C150.157 32 151.5 33.3431 151.5 35V64C151.5 65.6569 152.843 67 154.5 67H235.5';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <motion.path d={path} stroke="url(#paint0_linear)" />
      {/* <rect
          d={path}
          x={0}
          y={0}
          width={width}
          height={height}
          fill="url(#paint0_linear)"
        /> */}
      <defs>
        <motion.linearGradient
          id="paint0_linear"
          initial={{
            x1: 280,
            y1: 0,
            x2: 400.5,
            y2: 50,
          }}
          animate={{
            x1: -100,
            y1: 0,
            x2: -100,
            y2: 0,
          }}
          transition={{
            duration: 2,
            ease: 'linear',
            repeat: Number.POSITIVE_INFINITY,
            repeatDelay: 2,
          }}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2EB9DF" stopOpacity="0" />
          <stop stopColor="#2EB9DF" />
          <stop offset="1" stopColor="#9E00FF" stopOpacity="0" />
        </motion.linearGradient>
      </defs>
    </svg>
  );
};

export const SkeletonOne = () => {
  return (
    <Link
      href="https://blog.fireworks.ai/fireworks-ai-achieves-soc-2-type-ii-and-hipaa-compliance-27d6ae695692"
      target="__blank"
      className="h-60 w-full flex justify-center items-center relative p-4 rounded-lg "
    >
      <div className="absolute inset-0 bg-grid-small-zinc-200 dark:bg-grid-small-zinc-700" />
      <div className="flex justify-center items-center space-x-4 relative z-20">
        <Image
          src="/images/logos/brand/hipaa.png"
          height="500"
          width="500"
          className="rounded-lg h-24 w-24 object-contain hover:scale-105 transition duration-200"
          alt="hipaa"
        />
        <Image
          src="/images/logos/brand/soc2.png"
          height="500"
          width="500"
          className="rounded-lg h-24 w-24 object-contain hover:scale-105 transition duration-200"
          alt="soc2"
        />
      </div>
      {/* <HeroPatternSmall />
      <div className="relative z-50">
        <Graph />
      </div>
      <div className="absolute inset-0 h-20 w-20 rounded-full m-auto flex items-center justify-center backdrop-blur-sm z-50 border border-neutral-300 dark:border-zinc-700">
        <IconLockAccess className="h-12 w-12 text-black dark:text-white" />
      </div> */}
      <div className="absolute inset-x-0 bottom-0 h-20 bg-gray-100 dark:bg-zinc-800 [mask-image:linear-gradient(to_bottom,transparent,white)] pointer-events-none" />
    </Link>
  );
};

export const Graph = () => {
  const chartdata2 = [
    {
      date: 'Jan 1',
      throughput: 10,
    },
    {
      date: 'Jan 2',
      throughput: 40,
    },
    {
      date: 'Jan 3',

      throughput: 90,
    },
    {
      date: 'Jan 4',

      throughput: 60,
    },
    {
      date: 'Jan 5',

      throughput: 100,
    },
    {
      date: 'Jan 6',

      throughput: 140,
    },
    {
      date: 'Jan 7',

      throughput: 80,
    },
    {
      date: 'Jan 8',

      throughput: 160,
    },
    {
      date: 'Jan 9',

      throughput: 180,
    },
    {
      date: 'Jan 10',

      throughput: 200,
    },
    {
      date: 'Jan 11',

      throughput: 155,
    },
    {
      date: 'Jan 12',

      throughput: 160,
    },
  ];

  const valueFormatter = (v: any) => {
    return v;
  };

  return (
    <AreaChart
      className="h-60 mt-4"
      data={chartdata2}
      index="date"
      categories={['throughput']}
      colors={['indigo']}
      yAxisWidth={30}
      connectNulls={true}
      style={{ borderRadius: '10px' }}
      valueFormatter={valueFormatter}
      showAnimation={true}
      animationDuration={2000}
      showGridLines={false}
      curveType="monotone"
      showLegend={false}
      showYAxis={false}
      showXAxis={false}
    />
  );
};
